import { apiClient } from "@/http";
import { apiRoutes } from "./apiRoutes";

/**
 * 라벨 값을 필터 드롭다운 단위로 묶는 갈래.
 *
 * `code` 만 안정 식별자다. `display_name` · `description` · `sort_order` · 소속은 운영자가
 * `PATCH /v1/label-categories` 로 배포 없이 고칠 수 있다. 이 값들의 SoT 는 DB 다. 그래서 FE 는
 * 이름을 하드코딩하지 않고 이 응답으로 그린다.
 *
 * `description` 은 "무엇으로 구분하는가" 를 설명하는 산문이다. 라벨링 가이드가 갈래 머리에
 * 그대로 표시한다. 설명이 없는 갈래는 null 이다.
 *
 * `values` 는 소속 라벨 value 의 평면 배열이다. 에피소드 facets 응답에는 이 값이 없다.
 * 그래서 "이 갈래에 어떤 태그가 들어 있나"는 이 리소스에서만 알 수 있다.
 */
export type LabelCategory = {
  uuid: string;
  key: string;
  code: string;
  display_name: string;
  description: string | null;
  sort_order: number;
  values: readonly string[];
};

// key 별 세션 캐시. 정적 카탈로그이고 key 당 항목이 한 자릿수라 페이지가 없다.
const categoryCache = new Map<string, readonly LabelCategory[]>();
const categoryPromises = new Map<string, Promise<readonly LabelCategory[]>>();
// 세대 번호. 무효화 전에 보낸 in-flight 응답이 캐시를 다시 채우지 못하게 한다(`label.ts` 와 같은 규약).
let categoryEpoch = 0;

/**
 * 한 key 의 갈래 카탈로그. 세션당 1회 받고 이후 캐시에서 답한다.
 *
 * ⚠️ 서버는 `PageResp` 가 아니라 **bare 배열**을 보낸다. `items` 를 찾으면 안 된다.
 * 서버가 `(key, sort_order, code)` 로 정렬해 준다. 그래서 FE 는 다시 정렬하지 않는다.
 *
 * 실패는 캐시하지 않고 **그대로 throw 한다**. 화면에 맞는 degrade 는 소비 훅이 고른다.
 * api 층은 제품의 degrade 를 미리 정하지 않는다. `reviewOptions` 는 동기 스냅샷 소비처가 있어서
 * 빈 어휘를 돌려준다. 여기는 그런 제약이 없다.
 *
 * @param key 라벨 key. 예) `quality`
 * @returns 그 key 의 갈래 목록.
 */
export function listLabelCategories(key: string): Promise<readonly LabelCategory[]> {
  const cached = categoryCache.get(key);
  if (cached !== undefined) {
    return Promise.resolve(cached);
  }
  const inFlight = categoryPromises.get(key);
  if (inFlight !== undefined) {
    return inFlight;
  }

  const requestEpoch = categoryEpoch;
  const promise = apiClient
    .get<readonly LabelCategory[]>(apiRoutes.label.categories, { params: { key } })
    .then((response) => {
      const categories = response.data;
      if (requestEpoch === categoryEpoch) {
        categoryCache.set(key, categories);
      }
      return categories;
    })
    .finally(() => {
      if (categoryPromises.get(key) === promise) {
        categoryPromises.delete(key);
      }
    });

  categoryPromises.set(key, promise);
  return promise;
}

/**
 * 갈래 캐시를 비운다.
 *
 * `values` 는 라벨 행에서 파생된다. 그래서 세션(=조직) 경계가 바뀔 때와 갈래·소속을 편집할 때
 * 이 캐시를 비워야 한다.
 */
export function invalidateLabelCategories(): void {
  categoryEpoch += 1;
  categoryCache.clear();
  categoryPromises.clear();
}
